import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, UtensilsCrossed, Heart, Navigation, Hotel, MessageCircle } from "lucide-react";
import BottomTabNav from "../components/BottomTabNav";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const scenarios = [
  {
    id: "emergency",
    icon: AlertCircle,
    emoji: "🚨",
    title: "Emergency Help",
    description: "Medical, police, urgent situations",
    color: "from-red-500 to-red-600",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
    urgent: true,
    testId: "scenario-emergency"
  },
  {
    id: "food",
    icon: UtensilsCrossed,
    emoji: "🍽️",
    title: "Ordering Food",
    description: "Restaurants, menus, dietary needs",
    color: "from-orange-500 to-amber-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    testId: "scenario-food"
  },
  {
    id: "date",
    icon: Heart,
    emoji: "💕",
    title: "Going on a Date",
    description: "Casual conversation, cultural phrasing",
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-50",
    borderColor: "border-pink-200",
    testId: "scenario-date"
  },
  {
    id: "directions",
    icon: Navigation,
    emoji: "🧭",
    title: "Need Directions",
    description: "Taxi, rideshare, finding places",
    color: "from-blue-500 to-sky-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    testId: "scenario-directions"
  },
  {
    id: "hotel",
    icon: Hotel,
    emoji: "🏨",
    title: "Hotel / Travel Help",
    description: "Check-in, room issues, travel questions",
    color: "from-teal-500 to-cyan-600",
    bgColor: "bg-teal-50",
    borderColor: "border-teal-200",
    testId: "scenario-hotel"
  },
  {
    id: "general",
    icon: MessageCircle,
    emoji: "💬",
    title: "General Conversation",
    description: "Free-form translation",
    color: "from-[#0EA5E9] to-[#06b6d4]",
    bgColor: "bg-slate-50",
    borderColor: "border-slate-200",
    testId: "scenario-general"
  }
];

const ScenarioLanding = () => {
  const navigate = useNavigate();

  const handleScenarioSelect = (scenario) => {
    if (scenario.urgent) {
      // For emergency, offer immediate choice
      const goLive = window.confirm(
        "Emergency detected. Would you like to connect to a live interpreter immediately?\n\nClick OK for live help, or Cancel to use AI translation first."
      );
      
      if (goLive) {
        navigate("/live-help");
        return;
      }
    }
    
    navigate("/translate", { state: { scenario: scenario.id } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6 pb-32">
      <div className="max-w-4xl mx-auto">
        {/* Header with Logo */}
        <div className="flex flex-col items-center mb-8 mt-4">
          <img 
            src={LOGO_URL} 
            alt="PassportTalk logo" 
            className="w-20 h-20 mb-4 object-contain"
          />
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-travel-navy font-outfit text-center" data-testid="scenario-title">
              How can PassportTalk help you right now?
            </h1>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-semibold rounded-full">Beta</span>
          </div>
          <p className="text-slate-600 text-center text-sm sm:text-base max-w-xl">
            Choose a situation. AI helps first. A live human is one tap away.
          </p>
        </div>

        {/* Scenario Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {scenarios.map((scenario) => {
            const Icon = scenario.icon;
            return (
              <button
                key={scenario.id}
                onClick={() => handleScenarioSelect(scenario)}
                className={`${scenario.bgColor} ${scenario.borderColor} border-2 rounded-2xl p-6 text-left transition-all hover:shadow-lg hover:scale-[1.02] active:scale-95 relative overflow-hidden group`}
                data-testid={scenario.testId}
              >
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${scenario.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                
                {/* Urgent badge for emergency */}
                {scenario.urgent && (
                  <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <span className="animate-pulse">●</span>
                    Live help recommended
                  </div>
                )}

                <div className="relative z-10">
                  <div className="flex items-start gap-4 mb-3">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${scenario.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <span className="text-2xl">{scenario.emoji}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-travel-navy font-outfit mb-1">
                        {scenario.title}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {scenario.description}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom info banner */}
        <div className="bg-gradient-to-r from-[#0EA5E9]/10 to-[#06b6d4]/10 border-2 border-[#0EA5E9]/20 rounded-2xl p-6 text-center">
          <p className="text-sm text-travel-navy font-medium mb-2">
            ✨ Live interpreter available anytime
          </p>
          <p className="text-xs text-slate-600">
            Start with AI translation, escalate to human help instantly if needed.
          </p>
        </div>

        {/* Legal disclaimer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500">
            PassportTalk (Beta). Language assistance only. Not medical or legal advice.
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Currently in beta. Features and availability may change.
          </p>
        </div>
      </div>

      <BottomTabNav />
    </div>
  );
};

export default ScenarioLanding;
